<template>
    <div class="w-full h-full p-2">
        <ag-grid-vue
            v-if="ledger"
            class="h-full w-full"
            :theme="agTheme"
            :grid-options="gridOptions"
        />
    </div>
</template>

<script setup lang="ts">
import {AgGridVue} from 'ag-grid-vue3'

import {GridOptions, ICellRendererParams, IGetRowsParams, RowClassParams, ValueGetterParams} from 'ag-grid-community'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {formatCurrency, formattedDate} from '@/services/formatter.service'
import GenLegTransNameCell from '@/genleg/GenLegTransNameCell.vue'
import GenLegAttachmentCell from '@/genleg/GenLegAttachmentCell.vue'
import {agTheme} from '@/agUtils/ag-theme.service'
import {getTransactionLinesByIGetRowsParams} from '@/transaction/transaction.service'
import {LedgerAccount} from '@/types/reports/LedgerAccount'
import {computed, inject, Ref} from 'vue'
import {TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import {useVatService} from '@/vat/vat.service'
import TransactionInvoiceCell from '@/invoice/TransactionInvoiceCell.vue'
import GenLegSubAccountCell from '@/genleg/GenLegSubAccountCell.vue'

const props = defineProps({
    params: {
        type: Object as () => {
            data: LedgerAccount,
            context: {
                panelId: Number,
                panelPathSegment: String,
                searchParams: Ref<{
                    fromDate: string,
                    toDate: string
                }>,
                setActiveAccount: Function,
                activeAccountNo: Ref<string>
            }
        }
    }
})
const {getVatOptionById} = useVatService()
const ledger = computed<LedgerAccount>(() => props.params!.data)
const searchParams = computed<TransactionSearchParams>(() => props.params!.context.searchParams.value)
const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<string> | undefined = inject('panelPathSegment')
const gridOptions: GridOptions = {
    getRowId: ({data}: { data: TransactionLine }) => data.uuid,
    context: {
        panelId,
        panelPathSegment
    },
    rowHeight: 29,
    columnDefs: [
        {
            headerName: 'Dato',
            width: 100,
            field: 'date',
            cellRenderer: ({value}: { value: string }) => formattedDate(value),
            sortable: true,
            sort: 'asc'
        },
        {
            headerName: 'Bilag',
            width: 100,
            field: 'transactionName',
            cellRenderer: GenLegTransNameCell,
            contextMenuItems: []
        },
        {
            headerName: 'Beskrivelse',
            flex: 1,
            field: 'description',
            contextMenuItems: []
        },
        {
            headerName: 'Kunde/Leverandør',
            width: 200,
            field: 'subAccountUuid',
            cellRenderer: GenLegSubAccountCell,
        },
        {
            headerName: 'Faktura Nr',
            width: 100,
            field: 'invoiceUuid',
            cellRenderer: TransactionInvoiceCell
        },
        {
            headerName: 'Vedlegg',
            field: 'attachment',
            width: 200,
            minWidth: 70,
            cellRenderer: GenLegAttachmentCell,
            contextMenuItems: []
        },
        {
            headerName: 'MVA',
            width: 90,
            valueGetter: ({data}: ValueGetterParams) => {
                const vatId = data?.vatOptionId
                if (vatId > 1) {
                    const vatOption = getVatOptionById(vatId)
                    return vatOption?.code
                }
            }
        },
        {
            headerName: 'Beløp',
            width: 120,
            field: 'amount',
            headerClass: 'header-right',
            cellClass: 'text-right header-right',
            cellClassRules: {
                'font-bold': ({data}: { data: any }) => {
                    return ['incoming-balance', 'outgoing-balance', 'movement'].includes(data?.type)
                },
                'text-red-500 line-through': ({data}: { data: any }) => {
                    return data?.deleted
                }
            },
            cellRenderer: ({value}: ICellRendererParams) => formatCurrency(value),
            filter: 'agNumberColumnFilter',
            sortable: true,
            cellStyle: {overflow: 'visible'}

        }
    ],
    datasource: {
        rowCount: ledger.value.numTransactionLines,
        getRows: async (innerParams: IGetRowsParams) => {
            innerParams.filterModel = {
                accountNo: {
                    filterType: 'text',
                    type: 'equals',
                    filter: ledger.value.accountNo
                },
                date: {
                    filterType: 'join',
                    type: 'and',
                    conditions: [
                        {
                            filterType: 'date',
                            type: 'lessThanOrEqual',
                            dateFrom: searchParams.value.toDate
                        },
                        {
                            filterType: 'date',
                            type: 'greaterThanOrEqual',
                            dateFrom: searchParams.value.fromDate
                        }
                    ]
                },
                ...innerParams.filterModel
            }
            const tls = await getTransactionLinesByIGetRowsParams({
                ...innerParams,
                search: searchParams.value.query
            })
            innerParams.successCallback(tls, ledger.value.numTransactionLines)
        }
    },
    pinnedTopRowData: [
        {
            date: searchParams.value.fromDate,
            uuid: 'incoming-balance',
            description: 'Inngående saldo',
            type: 'incoming-balance',
            amount: ledger.value.incomingBalance
        }
    ],
    pinnedBottomRowData: [
        {
            date: searchParams.value.toDate,
            uuid: 'outgoing-balance',
            description: 'Utgående saldo',
            type: 'outgoing-balance',
            amount: ledger.value.outgoingBalance
        },
        {
            uuid: 'sum-changes',
            description: 'Endring i perioden',
            type: 'sum-changes',
            amount: ledger.value.movement
        }
    ],
    getRowClass: (params: RowClassParams) => {
        if (['incoming-balance', 'outgoing-balance', 'sum-changes'].includes(params.data?.type)) {
            return '!bg-base-200'
        } else {
            return '!bg-base-100'
        }
    },
    rowModelType: 'infinite'
}

</script>

<style scoped>

</style>